import { Component, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

function ErrorBoundaryFallback() {
  const { t } = useTranslation('common');

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
      <div className="bg-[#f8f8f8] rounded-[16px] p-[32px] max-w-[500px] w-full text-center">
        <h2 className="font-manrope font-bold text-[24px] leading-[32px] text-[#0d0d0d] mb-[12px]">
          {t('errorBoundary.title')}
        </h2>
        <p className="font-segoe text-[16px] leading-[24px] text-[#565858] mb-[24px]">
          {t('errorBoundary.message')}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="bg-[#2197f1] hover:bg-[#1a7bc7] rounded-[16px] px-[24px] py-[12px] transition-colors"
        >
          <span className="font-segoe font-semibold text-[16px] leading-[24px] text-white">
            {t('errorBoundary.reload')}
          </span>
        </button>
      </div>
    </div>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <ErrorBoundaryFallback />;
    }

    return this.props.children;
  }
}
